import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import Icon from '../AppIcon';

const Footer = () => {
  const { isDark } = useTheme();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  
  const currentYear = new Date().getFullYear();

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setTimeout(() => {
        setSubscribed(false);
        setEmail('');
      }, 3000);
    }
  };

  const socialLinks = [
    { name: 'GitHub', icon: 'Github', url: 'https://www.github.com/SE-LAPS', color: 'hover:text-gray-900 dark:hover:text-white' },
    { name: 'LinkedIn', icon: 'Linkedin', url: 'https://www.linkedin.com/in/lahiru-senavirathna-39b11a215', color: 'hover:text-blue-600' },
    { name: 'Twitter', icon: 'Twitter', url: 'https://www.twitter.com/LahiruSenavira5?s=09', color: 'hover:text-blue-400' },
    { name: 'Facebook', icon: 'Facebook', url: 'https://www.facebook.com/CodeShowLapZ?mibextid=ZbWKwL', color: 'hover:text-blue-500' },
    { name: 'Instagram', icon: 'Instagram', url: 'https://www.instagram.com/lahiru_senavirathna?igshid=MWtvMHI1cHJrY2RybA==', color: 'hover:text-pink-500' },
    { name: 'YouTube', icon: 'Youtube', url: 'https://www.youtube.com/@CodeShowLapZ', color: 'hover:text-red-500' },
  ];

  const services = [
    'Web Development',
    'Mobile App Development',
    'UI/UX Design',
    'Backend Development',
    'API Integration',
    'Database Design'
  ];
  
  return (
    <footer className={`relative mt-16 ${isDark ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'}`}>
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-600 via-blue-500 to-purple-600"></div>
      
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <a href="https://www.facebook.com/CodeShowLapZ?mibextid=ZbWKwL" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 mb-4 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-500 rounded-lg flex items-center justify-center">
                <Icon name="Code" size={24} className="text-white" />
              </div>
              <h3 className={`text-2xl font-bold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                CodeShow LapZ
              </h3>
            </a>
            <p className={`mb-6 leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Full-stack developer specializing in creating innovative web and mobile solutions. 
              Passionate about clean code, modern technologies, and exceptional user experiences.
            </p>
            
            {/* Social Links */}
            <div className="mb-6">
              <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>
                Follow Me
              </h4>
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                      isDark 
                        ? 'bg-slate-800 text-slate-400 hover:bg-slate-700' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } ${social.color}`}
                    aria-label={social.name}
                  >
                    <Icon name={social.icon} size={18} />
                  </a>
                ))}
              </div>
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
              Quick Links
            </h3>
            <ul className="space-y-2">
              {[
                { to: '/', label: 'Home' },
                { to: '/about-professional-story-center', label: 'About' },
                { to: '/projects-innovation-laboratory', label: 'Projects' },
                { to: '/skills-technical-mastery-showcase', label: 'Skills' },
                { to: '/experience-career-journey-timeline', label: 'Experience' },
                { to: '/contact-collaboration-gateway', label: 'Contact' }
              ].map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.to} 
                    className={`inline-flex items-center group transition-all duration-300 ${
                      isDark ? 'text-slate-300 hover:text-purple-400' : 'text-slate-600 hover:text-purple-600'
                    }`}
                  >
                    <Icon name="ChevronRight" size={14} className="mr-1 group-hover:translate-x-1 transition-transform" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Services */}
          <div>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
              Services
            </h3>
            <ul className="space-y-2">
              {services.map((service, index) => (
                <li key={index} className={`flex items-center ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  <Icon name="CheckCircle" size={14} className="mr-2 text-purple-500" />
                  <span className="text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Newsletter & Contact */}
          <div>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
              Stay Updated
            </h3>
            <p className={`text-sm mb-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Subscribe to get latest updates on new projects and tech insights.
            </p>
            
            {/* Newsletter Form */}
            <form onSubmit={handleSubscribe} className="mb-6">
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className={`w-full px-4 py-2 pr-12 rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all ${
                    isDark 
                      ? 'bg-slate-800 border-slate-700 text-slate-100 placeholder-slate-400' 
                      : 'bg-white border-gray-300 text-slate-900 placeholder-gray-400'
                  }`}
                  required
                />
                <button
                  type="submit"
                  className="absolute right-1 top-1 bottom-1 px-3 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-md hover:from-purple-700 hover:to-blue-600 transition-all"
                  aria-label="Subscribe"
                >
                  <Icon name="Send" size={16} />
                </button>
              </div>
              {subscribed && (
                <p className="text-green-500 text-xs mt-2 flex items-center">
                  <Icon name="CheckCircle" size={12} className="mr-1" />
                  Successfully subscribed!
                </p>
              )}
            </form>

            {/* Contact Info */}
            <div className="space-y-2">
              <div className={`flex items-start ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                <Icon name="Mail" size={16} className="mr-2 mt-1 text-purple-500" />
                <div>
                  <p className="text-xs font-medium">Email</p>
                  <a href="mailto:lahiru@codeshow.com" className="text-sm hover:text-purple-500 transition-colors">
                    lahiru@codeshow.com
                  </a>
                </div>
              </div>
              <div className={`flex items-start ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                <Icon name="Phone" size={16} className="mr-2 mt-1 text-purple-500" />
                <div>
                  <p className="text-xs font-medium">Phone</p>
                  <a href="tel:+94712435202" className="text-sm hover:text-purple-500 transition-colors">
                    +94 71 243 5202
                  </a>
                </div>
              </div>
              <div className={`flex items-start ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                <Icon name="MapPin" size={16} className="mr-2 mt-1 text-purple-500" />
                <div>
                  <p className="text-xs font-medium">Location</p>
                  <p className="text-sm">Colombo, Sri Lanka</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className={`pt-8 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0">
            <div className="flex space-x-6 text-sm">
              {[
                { label: 'Privacy Policy', href: '#' },
                { label: 'Terms of Service', href: '#' },
                { label: 'Cookie Policy', href: '#' }
              ].map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className={`transition-colors ${
                    isDark ? 'text-slate-400 hover:text-purple-400' : 'text-slate-500 hover:text-purple-600'
                  }`}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
          
          {/* Copyright Section */}
          <div className="mt-6 text-center">
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              &copy; {currentYear}{' '}
              <a 
                href="https://www.facebook.com/CodeShowLapZ?mibextid=ZbWKwL" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`font-semibold transition-colors ${
                  isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
                }`}
              >
                CodeShow LapZ
              </a>
              . All rights reserved. | Crafted with passion in Sri Lanka 🇱🇰
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;